import { useMemo, useRef } from 'react'
import {
  useCreatePOLineItemMatch,
  useDeletePOLineItemMatch,
  useDeletePOLineItemMatches,
  useGetPOLineItemMatchReasons,
  useGetPOLineItemMatches,
} from '@/service-library/hooks/po-matches'
import {
  useGetLiveData,
  useGetPDPurchaseOrders,
} from '@/services/hooks/relay/dynamic'
import { PDPurchaseOrder } from '@/types/po-match'
import { BaseVendor } from '@/types/relay'
import {
  PURCHASE_ORDER_INFO_TYPE_ID,
  VENDOR_INFO_TYPE_IDS,
} from '@/utils/info-types'
import { showErrorSnackbar } from '@/utils/snackbars'

export const USER_MATCHED_REASON_ID = 'c566630e-0f32-4152-94f1-d92ca266e378'

type UsePOMatchDataOptions = {
  documentId: string
  ownerOrgId: string
  orgCredentialTypeId: string
  userAddedPOs: Map<string, PDPurchaseOrder>
}

export default function usePOMatchData({
  documentId,
  ownerOrgId,
  orgCredentialTypeId,
  userAddedPOs,
}: UsePOMatchDataOptions) {
  const { poLineItemMatchReasons } = useGetPOLineItemMatchReasons()

  const reasonMap = useMemo(
    () => new Map(poLineItemMatchReasons.map((r) => [r.id, r.name])),
    [poLineItemMatchReasons],
  )

  // Fetch line item matches directly by document ID
  const {
    poLineItemMatches,
    queryKey: poLineItemMatchesQueryKey,
    isPending: isPendingLineItemMatches,
  } = useGetPOLineItemMatches({
    filters: {
      limit: '1000',
      document_row__document_id: documentId,
    },
    enabled: !!documentId,
  })

  const { deletePOLineItemMatch } = useDeletePOLineItemMatch({
    listQueryKey: poLineItemMatchesQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to delete line item match. Please try again.')
    },
  })

  const { deletePOLineItemMatches } = useDeletePOLineItemMatches({
    listQueryKey: poLineItemMatchesQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to remove PO match. Please try again.')
    },
  })

  // Derive matched PO IDs from line item matches
  const matchedPoIds = useMemo(
    () => new Set(poLineItemMatches.map((lim) => lim.po_id)),
    [poLineItemMatches],
  )

  // Group line item matches by po_id
  const poLineItemMatchPerPoId = useMemo(
    () => Map.groupBy(poLineItemMatches, (lim) => lim.po_id),
    [poLineItemMatches],
  )

  // Combines saved PO matches and user-added, in-memory-only PO matches.
  // Uses a ref to maintain stable insertion order — once a PO appears, its
  // position doesn't change (e.g. when a user-added PO becomes a saved match).
  // The ref only grows (appends new IDs) and never removes, so transient gaps
  // (e.g. a PO briefly absent from both sets during a render) don't lose order.
  const stableOrderRef = useRef<string[]>([])
  const userAddedPoIds = useMemo(
    () => new Set(userAddedPOs.keys()),
    [userAddedPOs],
  )
  const poIds = useMemo(() => {
    const allIds = matchedPoIds.union(userAddedPoIds)
    // Append new IDs; never remove from the stable order ref
    const newIds = allIds.difference(new Set(stableOrderRef.current))
    stableOrderRef.current = [...stableOrderRef.current, ...newIds]
    // Return only currently-active IDs, preserving stable order
    return stableOrderRef.current.filter((id) => allIds.has(id))
  }, [matchedPoIds, userAddedPoIds])

  // Fetch POs
  const {
    pdPurchaseOrders: fetchedPurchaseOrders,
    isPending: isLoadingPurchaseOrders,
  } = useGetPDPurchaseOrders({
    infoTypeId: PURCHASE_ORDER_INFO_TYPE_ID,
    orgId: ownerOrgId,
    orgCredentialTypeId,
    filters: {
      ids: poIds.join(','),
    },
    enabled: poIds.length > 0,
    placeholderData: (previousData) => {
      if (!previousData || poIds.length === 0) return undefined
      const currentIds = new Set(poIds)
      const previousIds = new Set(
        previousData.pages.flatMap((page) =>
          page.results.map((po) => po.external_id),
        ),
      )
      // Use previous data when adding POs (all previous results still relevant)
      // or when removing POs (all current IDs covered by previous data).
      const allPreviousRelevant = previousIds.isSubsetOf(currentIds)
      const allCurrentCovered = currentIds.isSubsetOf(previousIds)
      return allPreviousRelevant || allCurrentCovered ? previousData : undefined
    },
  })

  const isLoadingPOs =
    isPendingLineItemMatches || (poIds.length > 0 && isLoadingPurchaseOrders)

  // Merge fetched POs with user-added POs (user-added take priority for instant display)
  const pdPurchaseOrders = useMemo(() => {
    const fetchedMap = new Map(
      fetchedPurchaseOrders.map((po) => [po.external_id, po]),
    )
    // Merge: fetched data wins if available (more up-to-date), otherwise use user-added
    const merged = new Map(fetchedMap)
    for (const [id, po] of userAddedPOs) {
      if (!merged.has(id)) {
        merged.set(id, po)
      }
    }
    return [...merged.values()]
  }, [fetchedPurchaseOrders, userAddedPOs])

  // Sort POs to match the stable insertion order in poIds, filtering out any
  // stale entries that may linger in placeholder data after a PO is removed.
  const sortedPurchaseOrders = useMemo(() => {
    const orderMap = new Map(poIds.map((id, i) => [id, i]))
    return [...pdPurchaseOrders]
      .filter((po) => orderMap.has(po.external_id))
      .sort(
        (a, b) =>
          (orderMap.get(a.external_id) ?? Infinity) -
          (orderMap.get(b.external_id) ?? Infinity),
      )
  }, [pdPurchaseOrders, poIds])

  const { createPOLineItemMatch, isPending: isCreatingLineItemMatch } =
    useCreatePOLineItemMatch({
      listQueryKey: poLineItemMatchesQueryKey,
      onError: () => {
        showErrorSnackbar('Failed to create line item match. Please try again.')
      },
    })

  // Fetch all vendors
  const vendorIds = useMemo(
    () => pdPurchaseOrders.map((po) => po.vendor_id).filter((id) => id),
    [pdPurchaseOrders],
  )
  const { allData: vendors } = useGetLiveData<BaseVendor>({
    infoTypeId: VENDOR_INFO_TYPE_IDS[0],
    orgId: ownerOrgId,
    orgCredentialTypeId,
    filters: {
      ids: vendorIds.join(','),
      fields__only: 'entity_id,pd_display_name',
    },
    enabled: vendorIds.length > 0,
    placeholderData: (previousData) => previousData,
  })

  const vendorMap = useMemo(
    () => new Map(vendors.map((v) => [v.entity_id, v.pd_display_name])),
    [vendors],
  )

  return {
    poIds,
    pdPurchaseOrders,
    sortedPurchaseOrders,
    vendorMap,
    reasonMap,
    isLoadingPOs,
    isCreatingLineItemMatch,
    poLineItemMatchPerPoId,
    poLineItemMatchesQueryKey,
    deletePOLineItemMatch,
    deletePOLineItemMatches,
    createPOLineItemMatch,
  }
}
